import { FormEvent, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { OfferSlot } from "@/components/OfferSlot";

interface AlertsFormProps {
  effectiveTitle: string;
  urlLocation: string;
  urlRemote: boolean;
  urlInternship: boolean;
  urlContract: boolean;
  urlPartTime: boolean;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const GENERIC_ERROR = "Something went wrong. Please try again.";

const mapError = (status: number | undefined, message: string | undefined): string => {
  const msg = (message || "").toLowerCase();
  if (status === 429 || msg.includes("rate") || msg.includes("too many")) {
    return "Too many requests. Please wait a moment and try again.";
  }
  if (status === 400 && msg.includes("job title or company name")) {
    return "Please search for a role or location first.";
  }
  if (status === 400 && msg.includes("email")) {
    return "Please enter a valid email address.";
  }
  return GENERIC_ERROR;
};

const AlertsForm = ({
  effectiveTitle,
  urlLocation,
  urlRemote,
  urlInternship,
  urlContract,
  urlPartTime,
}: AlertsFormProps) => {
  const [frequency, setFrequency] = useState<"daily" | "weekly">("daily");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // 1. Local validation FIRST — before any network call.
    const hasAnyFilter = Boolean(
      effectiveTitle || urlLocation || urlRemote || urlInternship || urlContract || urlPartTime
    );
    if (!hasAnyFilter) {
      setError("Search for a role or location first, then we'll alert you when matches hit the board.");
      return;
    }
    if (!EMAIL_RE.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }

    setSubmitting(true);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke("create-job-alert", {
        body: {
          jobTitle: effectiveTitle || undefined,
          location: urlLocation || undefined,
          isRemote: urlRemote,
          isInternship: urlInternship,
          isContract: urlContract,
          isPartTime: urlPartTime,
          email: email.trim(),
          frequency,
          source: "board_alerts_form",
        },
      });

      if (invokeError) {
        // FunctionsHttpError exposes context.status when available.
        const status = (invokeError as any)?.context?.status as number | undefined;
        setError(mapError(status, invokeError.message));
        setSubmitting(false);
        return;
      }

      if (data && (data as any).error) {
        setError(mapError(undefined, (data as any).error));
        setSubmitting(false);
        return;
      }

      setSuccess(true);
    } catch {
      setError(GENERIC_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="alerts__card">
        <div style={{ textAlign: "center", padding: "16px 8px" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }} aria-hidden="true">✓</div>
          <h3 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 8px" }}>Alert created</h3>
          <p style={{ margin: 0, color: "var(--af-grey-500, #64748b)" }}>
            Check your inbox for confirmation.
          </p>
        </div>
        {/* Remote alerts meet Still Remote; everyone else sees Candidate Pro. */}
        <div style={{ marginTop: 16 }}>
          <OfferSlot
            offerKey={urlRemote ? "still_remote" : "candidate_pro"}
            surface="alert_success"
            compact
          />
        </div>
      </div>
    );
  }

  return (
    <div className="alerts__card">
      <span className="alerts__label">Frequency</span>
      <div className="alerts__freq">
        <button
          type="button"
          className={`freq-pill${frequency === "daily" ? " is-active" : ""}`}
          onClick={() => setFrequency("daily")}
          disabled={submitting}
        >
          Daily digest
        </button>
        <button
          type="button"
          className={`freq-pill${frequency === "weekly" ? " is-active" : ""}`}
          onClick={() => setFrequency("weekly")}
          disabled={submitting}
        >
          Weekly digest
        </button>
      </div>

      <form className="alerts__input-row" onSubmit={handleSubmit}>
        <input
          type="email"
          className="alerts__input"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={submitting}
          required
        />
        <button type="submit" className="alerts__submit" disabled={submitting}>
          {submitting ? "Setting up..." : "Set up alerts →"}
        </button>
      </form>

      {error ? (
        <p className="alerts__error" role="alert">
          {error}
        </p>
      ) : (
        <p className="alerts__fineprint">One click to unsubscribe anytime.</p>
      )}
    </div>
  );
};

export default AlertsForm;
